import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useSelector } from "react-redux";
import Button from "components/atoms/Button";
import { PageMode } from "utils/constant";
import TableDataComponent from "components/organisms/TableData";
import { useState } from "react";
import LarvaBreeding from "services/LarvaBreeding";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import {
  errorToastNotif,
  setDataToLocalStorage,
  successToastNotif,
} from "utils/common";
import { confirmAlert } from "react-confirm-alert";
import ActionButton from "components/atoms/ActionButton";
import { BreadcrumbNav } from "components/molecules";
import { useHistory } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
  getLarvicideTypeDisplay,
  getLarvicideFormDisplay,
  getLarvicideAmountDisplay,
  getLarvicideUnitDisplay,
} from "../utils/larvicide";

const LarvaBreedingService = new LarvaBreeding();

const TableComponent = ({ changeMode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [modalImage, setModalImage] = useState("");
  const [qrModal, setQrModal] = useState({ isOpen: false, coordinate: "" });
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const { currentUser } = useSelector((state) => state.authReducer);
  const params = useParams();
  const history = useHistory();

  const fetchLarvaBreedingData = async () => {
    setIsLoading(true);
    try {
      const response = await LarvaBreedingService.getAll({
        searchBy: `vector_larva_identification_id`,
        searchValue: params.id,
      });

      setDataState((prevState) => ({
        ...prevState,
        data: response.vector_larva_interventions,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLarvaBreedingData();
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onEditData = (item) => {
    setDataToLocalStorage("SELECTED_ITEM", JSON.stringify(item));
    changeMode(PageMode.edit);
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await LarvaBreedingService.remove(id)
              .then((res) => {
                fetchLarvaBreedingData();
                successToastNotif(
                  "Data Kohort Berhasil Dihapus",
                );
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
      ],
    });
  };

  const getInterventionOthers = (interventions) => {
    if (!interventions || !Array.isArray(interventions)) return "-";
    const found = interventions.find((x) => typeof x === "string" && x.startsWith("Lainnya_"));
    return found ? found.replace("Lainnya_", "") : "-";
  };

  const getInterventionBelum = (interventions) => {
    if (!interventions || !Array.isArray(interventions)) return "-";
    const found = interventions.find((x) => typeof x === "string" && (x.startsWith("Belum_") || x === "Belum dilakukan Intervensi"));
    if (!found) return "-";
    return found.startsWith("Belum_") ? found.replace("Belum_", "") : "Belum dilakukan Intervensi";
  };

  const isSudahIntervensi = (interventions) => {
    if (!interventions || !Array.isArray(interventions) || interventions.length === 0) return "Belum";
    const anyActual = interventions.some((x) => typeof x === "string" && !x.startsWith("Belum_") && x !== "Belum dilakukan Intervensi");
    return anyActual ? "Sudah" : "Belum";
  };

  const handleViewImage = async (id, kind) => {
    try {
      const blob =
        kind === "larva"
          ? await LarvaBreedingService.getImageLarva(id)
          : await LarvaBreedingService.getImage(id);
      const fileURL = URL.createObjectURL(blob);
      setModalImage(fileURL);
    } catch (err) {
      console.log(err);
      // Viewing a photo is a load action, not a save; show a photo-specific message.
      toast.error("Gagal memuat foto. File tidak ditemukan atau terjadi kesalahan.", {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => onEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.id)}
            btnType={"delete"}
          />
        </div>
      ),
    },
    {
      header: "Barcode",
      accessorKey: "qr_barcode",
      cell: ({ row }) =>
        row.original.coordinate ? (
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() =>
              setQrModal({ isOpen: true, coordinate: row.original.coordinate })
            }
          >
            <FiEye style={{ marginRight: "6px" }} /> Lihat QR
          </Button>
        ) : (
          "-"
        ),
    },
    {
      header: "Follow-up Ke",
      accessorKey: "follow_up",
      cell: ({ row }) => row.index + 1,
    },
    {
      header: "Tanggal",
      accessorKey: "intervention_date",
    },
    {
      header: "Petugas",
      accessorKey: "officer_team",
      cell: ({ row }) => row.original.officer_team || "-",
    },
    {
      header: "Status TP",
      accessorKey: "status",
      cell: ({ row }) => row.original.status || "-",
    },
    {
      header: "Jentik?",
      accessorKey: "anopheles_larva_found",
      cell: ({ row }) => row.original.anopheles_larva_found || "-",
    },
    {
      header: "Jml Cidukan",
      accessorKey: "total_catch",
      cell: ({ row }) => row.original.total_catch ?? "-",
    },
    {
      header: "Jml Jentik",
      accessorKey: "total_larva",
      cell: ({ row }) => row.original.total_larva ?? "-",
    },
    {
      header: "Kepadatan",
      accessorKey: "density",
      cell: ({ row }) => {
        const catchVal = parseFloat(row.original.total_catch);
        const larvaVal = parseFloat(row.original.total_larva);
        if (!isNaN(catchVal) && !isNaN(larvaVal) && catchVal > 0) {
          return ((larvaVal / catchVal) * 100).toFixed(2);
        }
        return "-";
      },
    },
    {
      header: "Instar",
      accessorKey: "larva_phase",
      cell: ({ row }) => {
        const phase = row.original.larva_phase;
        if (Array.isArray(phase)) return phase.join(", ");
        return phase || "-";
      },
    },
    {
      header: "Luas (m2)",
      accessorKey: "area_breeding_place",
      cell: ({ row }) => row.original.area_breeding_place ?? "-",
    },
    {
      header: "Kedalaman (m)",
      accessorKey: "depth_breeding_place",
      cell: ({ row }) => row.original.depth_breeding_place ?? "-",
    },
    {
      header: "Jarak (m)",
      accessorKey: "distance_to_nearest_house",
      cell: ({ row }) => row.original.distance_to_nearest_house ?? "-",
    },
    {
      header: "Kekeruhan",
      accessorKey: "water_turbidity",
      cell: ({ row }) => row.original.water_turbidity || "-",
    },
    {
      header: "Tanaman Air",
      accessorKey: "plan_exist_breeding_place",
      cell: ({ row }) => row.original.plan_exist_breeding_place || "-",
    },
    {
      header: "Sampah",
      accessorKey: "rubbish_exist_breeding_place",
      cell: ({ row }) => row.original.rubbish_exist_breeding_place || "-",
    },
    {
      header: "Sifat TP",
      accessorKey: "trait_breeding_place",
      cell: ({ row }) => row.original.trait_breeding_place || "-",
    },
    {
      header: "Paparan Matahari",
      accessorKey: "sun_exposure",
      cell: ({ row }) => row.original.sun_exposure || "-",
    },
    {
      header: "Air Payau",
      accessorKey: "brackish_water",
      cell: ({ row }) => row.original.brackish_water || "-",
    },
    {
      header: "Air Mengalir",
      accessorKey: "running_water",
      cell: ({ row }) => row.original.running_water || "-",
    },
    {
      header: "Ada Ikan",
      accessorKey: "fish_exist",
      cell: ({ row }) => row.original.fish_exist || "-",
    },
    {
      header: "Intervensi",
      accessorKey: "is_intervened",
      cell: ({ row }) => isSudahIntervensi(row.original.intervention_done),
    },
    {
      header: "Larvasidasi",
      accessorKey: "has_larvacide",
      cell: ({ row }) =>
        Array.isArray(row.original.intervention_done) &&
        row.original.intervention_done.includes("Larvasidasi")
          ? "Ya"
          : "Tidak",
    },
    {
      header: "Jenis Larvasida",
      accessorKey: "type_larvicide",
      cell: ({ row }) => getLarvicideTypeDisplay(row.original),
    },
    {
      header: "Bentuk Larvasida",
      accessorKey: "form_larvicide",
      cell: ({ row }) => getLarvicideFormDisplay(row.original),
    },
    {
      header: "Jumlah Larvasida",
      accessorKey: "total_larvicide",
      cell: ({ row }) => getLarvicideAmountDisplay(row.original),
    },
    {
      header: "Satuan",
      accessorKey: "total_larvicide_unit",
      cell: ({ row }) => getLarvicideUnitDisplay(row.original),
    },
    {
      header: "Penebaran Ikan",
      accessorKey: "has_fish",
      cell: ({ row }) =>
        Array.isArray(row.original.intervention_done) &&
        row.original.intervention_done.includes("Penebaran Ikan")
          ? "Ya"
          : "Tidak",
    },
    {
      header: "Modifikasi",
      accessorKey: "has_modification",
      cell: ({ row }) =>
        Array.isArray(row.original.intervention_done) &&
        row.original.intervention_done.includes("Modifikasi Lingkungan")
          ? "Ya"
          : "Tidak",
    },
    {
      header: "Manipulasi",
      accessorKey: "has_manipulation",
      cell: ({ row }) =>
        Array.isArray(row.original.intervention_done) &&
        row.original.intervention_done.includes("Manipulasi Lingkungan")
          ? "Ya"
          : "Tidak",
    },
    {
      header: "Intervensi lainnya",
      accessorKey: "other_intervention",
      cell: ({ row }) => getInterventionOthers(row.original.intervention_done),
    },
    {
      header: "Belum Intervensi",
      accessorKey: "not_intervened",
      cell: ({ row }) =>
        Array.isArray(row.original.intervention_done) &&
        row.original.intervention_done.some(
          (x) => x.startsWith("Belum_") || x === "Belum dilakukan Intervensi"
        )
          ? "Ya"
          : "Tidak",
    },
    {
      header: "Rencana Intervensi",
      accessorKey: "plan_intervention",
      cell: ({ row }) => getInterventionBelum(row.original.intervention_done),
    },
    {
      header: "Foto TP",
      accessorKey: "image",
      cell: ({ row }) =>
        row.original.image ? (
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => handleViewImage(row.original.id, "tp")}
          >
            <FiEye style={{ marginRight: "6px" }} /> Lihat Foto
          </Button>
        ) : (
          "-"
        ),
    },
    {
      header: "Foto Jentik",
      accessorKey: "image_larva",
      cell: ({ row }) =>
        row.original.image_larva ? (
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => handleViewImage(row.original.id, "larva")}
          >
            <FiEye style={{ marginRight: "6px" }} /> Lihat Foto
          </Button>
        ) : (
          "-"
        ),
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <BreadcrumbNav
        items={[
          {
            label: "Data Identifikasi Jentik & Kohort",
            onClick: () => history.push("/data-identifikasi-jentik"),
          },
          {
            label: "Kohort",
          },
        ]}
      />
      <h1 className="page-title">Data Kohort</h1>
      <div
        className="action-wrapper"
        style={{ marginTop: "24px", marginBottom: "24px" }}
      >
        <div className="action-button">
          {currentUser?.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => changeMode(PageMode.add)}
            >
              Tambah Kohort
            </Button>
          )}
        </div>
      </div>
      {/* table component */}
      <TableDataComponent
        columnLength={columns.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
      {modalImage && (
        <div
          onClick={() => setModalImage("")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={() => setModalImage("")}
              style={{
                position: "absolute",
                top: "-12px",
                right: "-12px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                background: "#fff",
                color: "#333",
                cursor: "pointer",
                fontSize: "18px",
                lineHeight: "32px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              }}
              aria-label="Tutup"
            >
              &times;
            </button>
            <img
              src={modalImage}
              alt="Preview besar"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "8px",
                display: "block",
              }}
            />
          </div>
        </div>
      )}
      {qrModal.isOpen && (
        <div
          onClick={() => setQrModal({ isOpen: false, coordinate: "" })}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", background: "#fff", padding: "24px", borderRadius: "8px", textAlign: "center" }}
          >
            <button
              type="button"
              onClick={() => setQrModal({ isOpen: false, coordinate: "" })}
              style={{ position: "absolute", top: "-12px", right: "-12px", width: "32px", height: "32px", borderRadius: "50%", border: "none", background: "#fff", color: "#333", cursor: "pointer", fontSize: "18px", lineHeight: "32px", boxShadow: "0 2px 6px rgba(0,0,0,0.3)" }}
              aria-label="Tutup"
            >
              &times;
            </button>
            <h4 style={{ marginBottom: "20px" }}>QR Code Google Maps</h4>
            {qrModal.coordinate ? (
              <QRCodeSVG value={`https://www.google.com/maps?q=${qrModal.coordinate}`} size={200} />
            ) : (
              <p>Koordinat tidak tersedia.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
